import React, { Component } from "react";
import { Modal, Form, Input, Checkbox, message } from "antd";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-mysql";
import "ace-builds/src-noconflict/theme-chrome";
import "ace-builds/src-noconflict/ext-language_tools";
import { createQuery, updateQuery, toggleQueryShared, canShareQueries } from "../../../utils/sqlQueryService";

const { TextArea } = Input;

// Validation constants
const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_FOLDER_LENGTH = 200;

// Folder path can only contain alphanumeric, slashes, hyphens, and underscores
const FOLDER_PATH_REGEX = /^[a-zA-Z0-9/_-]*$/;

class SaveQueryModal extends Component {
  formRef = React.createRef();
  state = {
    queryValue: "",
    queryError: "",
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      // Reset form when modal opens
      if (this.formRef.current) {
        if (this.props.editQuery) {
          // Editing existing query
          const queryText = this.props.editQuery.query || "";
          this.setState({ queryValue: queryText });
          this.formRef.current.setFieldsValue({
            name: this.props.editQuery.name,
            description: this.props.editQuery.description || "",
            folder: this.props.editQuery.folder || "",
            isShared: this.props.editQuery.isShared || false,
          });
        } else {
          // New query
          const queryText = this.props.currentQuery || "";
          this.setState({ queryValue: queryText });
          this.formRef.current.resetFields();
        }
      }
    }
  }

  handleQueryChange = (value) => {
    this.setState({ 
      queryValue: value,
      queryError: "", // Clear error when user types
    });
  };

  validateQuery = () => {
    const { queryValue } = this.state;
    if (!queryValue || !queryValue.trim()) {
      this.setState({ queryError: "Please enter a SQL query" });
      return false;
    }
    this.setState({ queryError: "" });
    return true;
  };

  validateFolderPath = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.length > MAX_FOLDER_LENGTH) {
      return Promise.reject(new Error(`Folder path must be less than ${MAX_FOLDER_LENGTH} characters`));
    }
    if (!FOLDER_PATH_REGEX.test(value)) {
      return Promise.reject(new Error("Folder path can only contain letters, numbers, slashes (/), hyphens (-), and underscores (_)"));
    }
    // Check for leading/trailing slashes or double slashes
    if (value.startsWith('/') || value.endsWith('/') || value.includes('//')) {
      return Promise.reject(new Error("Folder path cannot start/end with slash or contain consecutive slashes"));
    }
    return Promise.resolve();
  };

  handleSave = async () => {
    try {
      const values = await this.formRef.current.validateFields();
      const { editQuery, onSave } = this.props;
      const { queryValue } = this.state;

      // Validate query is not empty
      if (!this.validateQuery()) {
        return;
      }

      if (editQuery) {
        // Update existing query (excluding isShared)
        await updateQuery(editQuery.id, {
          name: values.name,
          description: values.description,
          query: queryValue,
          folder: values.folder || '',
        });
        
        // Handle isShared status separately if it changed
        const newIsShared = values.isShared || false;
        if (editQuery.isShared !== newIsShared) {
          await toggleQueryShared(editQuery.id, newIsShared);
        }
        
        message.success("Query updated successfully");
      } else {
        // Create new query
        await createQuery({
          name: values.name,
          description: values.description,
          query: queryValue,
          folder: values.folder || '',
          isShared: values.isShared || false,
        });
        message.success("Query saved successfully");
      }

      if (onSave) {
        onSave();
      }
      this.props.onClose();
    } catch (error) {
      message.error(error.message || "Failed to save query");
    }
  };

  render() {
    const { visible, onClose, editQuery } = this.props;
    const { queryValue, queryError } = this.state;
    const isAdmin = canShareQueries();

    return (
      <Modal
        title={editQuery ? "Edit Saved Query" : "Save Query"}
        open={visible}
        onOk={this.handleSave}
        onCancel={onClose}
        okText={editQuery ? "Update" : "Save"}
        cancelText="Cancel"
        width={800}
      >
        <Form
          ref={this.formRef}
          layout="vertical"
          initialValues={{
            name: "",
            description: "",
            folder: "",
            isShared: false,
          }}
        >
          <Form.Item
            label="Query Name"
            name="name"
            rules={[
              { required: true, message: "Please enter a query name" },
              { 
                validator: (_, value) => {
                  if (value && !value.trim()) {
                    return Promise.reject(new Error("Query name cannot be only whitespace"));
                  }
                  return Promise.resolve();
                }
              },
              { max: MAX_NAME_LENGTH, message: `Name must be less than ${MAX_NAME_LENGTH} characters` },
            ]}
          >
            <Input placeholder="e.g., Get Active Users" maxLength={MAX_NAME_LENGTH} />
          </Form.Item>

          <Form.Item
            label="Folder / Path (Optional)"
            name="folder"
            tooltip="Organize queries in folders. Use slash for nested paths (e.g., build/csp/tools)"
            rules={[
              { validator: this.validateFolderPath },
            ]}
          >
            <Input placeholder="e.g., build/csp/tools" maxLength={MAX_FOLDER_LENGTH} />
          </Form.Item>

          <Form.Item
            label="Description (Optional)"
            name="description"
            rules={[
              { max: MAX_DESCRIPTION_LENGTH, message: `Description must be less than ${MAX_DESCRIPTION_LENGTH} characters` },
            ]}
          >
            <TextArea
              rows={2}
              placeholder="Brief description of what this query does..."
              maxLength={MAX_DESCRIPTION_LENGTH}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="SQL Query"
            required
            validateStatus={queryError ? "error" : ""}
            help={queryError}
          >
            <AceEditor
              mode="mysql"
              theme="chrome"
              value={queryValue}
              onChange={this.handleQueryChange}
              name="query-editor"
              editorProps={{ $blockScrolling: true }}
              width="100%"
              height="250px"
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                fontSize: 14,
                showLineNumbers: true,
                tabSize: 2,
              }}
              placeholder="Enter your SQL query here..."
              style={queryError ? { border: "1px solid #ff4d4f", borderRadius: "4px" } : {}}
            />
          </Form.Item>

          {isAdmin && (
            <Form.Item
              name="isShared"
              valuePropName="checked"
              tooltip="Shared queries are visible to all users"
            >
              <Checkbox>
                Share with all users (Admin only)
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>
    );
  }
}

export default SaveQueryModal;

